import React from 'react';
import Fade from 'react-reveal/Fade';

import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FormControlComponent } from 'design/formControl';
import { PressControlComponent } from 'design/pressControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import {
  INPUT_EMAIL_MAX_LENGTH,
  ANIMATION_DEFAULT_DURATION,
  INPUT_PASSWORD_MAX_LENGTH,
} from 'utils/constants';
import { validateEmailFormValues } from 'utils/emailFormUtils';
import { useServices } from 'hooks/useServices';
import { ParagraphComponent } from 'design/paragraph';
import { MarginComponent } from 'design/margin';
import {
  EmailAlreadyInUseError,
  ForbiddenError,
  InvalidEmailVerificationCodeError,
  UnauthorizedError,
} from 'utils/errors';
import { useEmailForm } from 'hooks/useEmailForm';
import { useEmailConfirmationForm } from 'hooks/useEmailConfirmationForm';
import {
  createEmailConfirmationFormValues,
  validateEmailConfirmationWithCodeFormValues,
  validateEmailConfirmationWithPasswordFormValues,
} from 'utils/emailConfirmationFormUtils';
import { SmallPressControlComponent } from 'design/smallPressControl';
import { CodeInputLayoutComponent } from 'design/codeInputLayout';
import { CharInputs } from 'containers/CharInputs';
import { SpinnerComponent } from 'design/spinner';
import { GridContainerComponent } from 'design/grid';
import { EmailConfirmationType } from 'types/state';
import { LinkControlComponent } from 'design/linkControl';
import { routes } from 'utils/routes';

type Props = {
  onEmailConfirmed(): void;
};

type ProcessName = 'EMAIL_FORM_SUBMISSION' | 'CONFIRMATION_FORM_SUBMISSION';

export const ImplicitAuthEmailForm = (props: Props) => {
  const { onEmailConfirmed } = props;

  const [processName, setProcessName] = React.useState<null | ProcessName>(
    null
  );

  const {
    sendAuthEmail,
    verifyEmailCode,
    getEmailConfirmationType,
    signIn,
  } = useServices();

  const intl = useIntlDictionary();

  const { implicitAuthForm: ownDictionary } = intl;

  const [
    confirmationType,
    setConfirmationType,
  ] = React.useState<null | EmailConfirmationType>(null);

  const {
    values: emailFormValues,
    violations: emailFormViolations,
    setViolations: setEmailFormViolations,
    setEmailValue,
  } = useEmailForm();

  const {
    values: confirmationFormValues,
    violations: confirmationFormViolations,
    setValues: setConfirmationFormValues,
    setViolations: setConfirmationFormViolations,
    setCodeValue,
    setPasswordValue,
  } = useEmailConfirmationForm();

  const { injectPromise } = useAsyncBoundary();

  const submitEmailForm = React.useCallback(() => {
    setEmailFormViolations([]);

    const violations = validateEmailFormValues(emailFormValues);

    if (violations.length > 0) {
      Promise.resolve(violations).then(setEmailFormViolations);
    } else {
      setProcessName('EMAIL_FORM_SUBMISSION');

      const promise = getEmailConfirmationType({
        emailAddress: emailFormValues.email,
      }).then((emailConfirmationType) => {
        if (emailConfirmationType === 'WITH_CODE') {
          return sendAuthEmail({
            emailAddress: emailFormValues.email,
          })
            .then(() => {
              setConfirmationType('WITH_CODE');
              setProcessName(null);
            })
            .catch((error) => {
              if (error instanceof EmailAlreadyInUseError) {
                setEmailFormViolations([
                  {
                    name: 'EmailAlreadyInUse',
                  },
                ]);
                setProcessName(null);
              } else {
                throw error;
              }
            });
        } else {
          setConfirmationType('WITH_PASSWORD');
          setProcessName(null);
        }
      });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [
    emailFormValues,
    getEmailConfirmationType,
    injectPromise,
    sendAuthEmail,
    setEmailFormViolations,
  ]);

  const submitConfirmationWithCodeForm = React.useCallback(() => {
    const violations = validateEmailConfirmationWithCodeFormValues(
      confirmationFormValues
    );

    if (violations.length === 0) {
      setProcessName('CONFIRMATION_FORM_SUBMISSION');

      const promise = verifyEmailCode({
        code: confirmationFormValues.code.join(''),
      })
        .then(() => {
          onEmailConfirmed();
        })
        .catch((error) => {
          if (error instanceof InvalidEmailVerificationCodeError) {
            setConfirmationFormValues(createEmailConfirmationFormValues());
            setConfirmationFormViolations([{ name: 'InvalidCode' }]);
            setProcessName(null);
          } else {
            throw error;
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [
    confirmationFormValues,
    injectPromise,
    onEmailConfirmed,
    setConfirmationFormValues,
    setConfirmationFormViolations,
    verifyEmailCode,
  ]);

  const submitConfirmationWithPasswordForm = React.useCallback(() => {
    const violations = validateEmailConfirmationWithPasswordFormValues(
      confirmationFormValues
    );

    if (violations.length > 0) {
      Promise.resolve(violations).then(setConfirmationFormViolations);
    } else {
      setProcessName('CONFIRMATION_FORM_SUBMISSION');

      const promise = signIn({
        email: emailFormValues.email,
        password: confirmationFormValues.password,
      })
        .then(() => {
          onEmailConfirmed();
        })
        .catch((error) => {
          const isInvalidPair =
            error instanceof UnauthorizedError ||
            error instanceof ForbiddenError;

          if (isInvalidPair) {
            setConfirmationFormViolations([
              {
                name: 'InvalidPair',
              },
            ]);
            setConfirmationFormValues((prev) => ({
              ...prev,
              password: '',
            }));
            setProcessName(null);
          } else {
            throw error;
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [
    confirmationFormValues,
    emailFormValues.email,
    injectPromise,
    onEmailConfirmed,
    setConfirmationFormValues,
    setConfirmationFormViolations,
    signIn,
  ]);

  const submit = React.useCallback(() => {
    if (confirmationType === null) {
      submitEmailForm();
    }

    if (confirmationType === 'WITH_PASSWORD') {
      submitConfirmationWithPasswordForm();
    }
  }, [confirmationType, submitConfirmationWithPasswordForm, submitEmailForm]);

  React.useEffect(() => {
    if (confirmationType === 'WITH_CODE') {
      submitConfirmationWithCodeForm();
    }
  }, [confirmationType, submitConfirmationWithCodeForm]);

  const changeEmail = React.useCallback(() => {
    setConfirmationType(null);
    setEmailValue('');
    setConfirmationFormViolations([]);
  }, [setConfirmationFormViolations, setEmailValue]);

  const emailHint = React.useMemo(() => {
    for (let violation of emailFormViolations) {
      switch (violation.name) {
        case 'EmailMissed':
          return intl.inputValidationRequired;
        case 'EmailMalformed':
          return intl.inputValidationEmail;
      }
    }
  }, [
    emailFormViolations,
    intl.inputValidationEmail,
    intl.inputValidationRequired,
  ]);

  const invalidDigitIndexes = React.useMemo(() => {
    if (confirmationFormViolations.length > 0) {
      return [0, 1, 2, 3, 4, 5];
    }
  }, [confirmationFormViolations]);

  const emailIsInvalid = emailFormViolations.length > 0;

  const passwordHint = React.useMemo(() => {
    for (let violation of confirmationFormViolations) {
      switch (violation.name) {
        case 'PasswordMissed':
          return intl.inputValidationRequired;
        case 'InvalidPair':
          return intl.inputValidationWrongEmailOrPassword;
      }
    }
  }, [
    intl.inputValidationRequired,
    intl.inputValidationWrongEmailOrPassword,
    confirmationFormViolations,
  ]);

  const passwordIsInvalid = React.useMemo(() => {
    for (let violation of confirmationFormViolations) {
      switch (violation.name) {
        case 'PasswordMissed':
        case 'InvalidPair':
          return true;
        default:
          return false;
      }
    }
  }, [confirmationFormViolations]);

  const emailAlreadyInUse = emailFormViolations.some(
    (violation) => violation.name === 'EmailAlreadyInUse'
  );

  const hasViolatedField =
    emailFormViolations.length > 0 || confirmationFormViolations.length > 0;

  return (
    <FormControlComponent
      shouldFocusFirstInvalidInput={hasViolatedField}
      onSubmit={submit}
    >
      <FloatingLabelComponent
        lifted={!!emailFormValues.email}
        invalid={emailIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {intl.emailInputPlaceholder}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {emailHint}
          </TextComponent>
        }
      >
        <GridContainerComponent>
          <TextInputControlComponent
            name="email"
            value={emailFormValues.email}
            disabled={processName === 'EMAIL_FORM_SUBMISSION'}
            invalid={emailIsInvalid}
            maxLength={INPUT_EMAIL_MAX_LENGTH}
            readOnly={confirmationType !== null}
            onValueChange={setEmailValue}
            autoFocus={!confirmationType}
          />
          {confirmationType !== null && (
            <SmallPressControlComponent
              disabled={processName === 'CONFIRMATION_FORM_SUBMISSION'}
              onPress={changeEmail}
            >
              {ownDictionary.changeEmailActionText}
            </SmallPressControlComponent>
          )}
        </GridContainerComponent>
      </FloatingLabelComponent>

      <Fade
        duration={ANIMATION_DEFAULT_DURATION}
        delay={
          confirmationType === 'WITH_CODE' ? ANIMATION_DEFAULT_DURATION : 0
        }
        when={confirmationType === 'WITH_CODE'}
        mountOnEnter
        unmountOnExit
        collapse
        bottom
      >
        <MarginComponent top={30}>
          <ParagraphComponent>
            {confirmationFormViolations.length > 0 ? (
              <TextComponent color="critical">
                {ownDictionary.codeInvalidLabel}
              </TextComponent>
            ) : (
              <TextComponent>{ownDictionary.codeLabel}</TextComponent>
            )}
          </ParagraphComponent>
          <CodeInputLayoutComponent
            theme="emailCode"
            inputs={
              <CharInputs
                name="emailCode"
                chars={confirmationFormValues.code}
                onCharsChange={setCodeValue}
                invalidCharIndexes={invalidDigitIndexes}
                disabled={processName === 'CONFIRMATION_FORM_SUBMISSION'}
                autoFocus={confirmationType === 'WITH_CODE'}
              />
            }
            indicator={
              processName === 'CONFIRMATION_FORM_SUBMISSION' && (
                <TextComponent color="accent">
                  <SpinnerComponent />
                </TextComponent>
              )
            }
          />
        </MarginComponent>
      </Fade>

      <Fade
        duration={ANIMATION_DEFAULT_DURATION}
        delay={
          confirmationType === 'WITH_PASSWORD' ? ANIMATION_DEFAULT_DURATION : 0
        }
        when={confirmationType === 'WITH_PASSWORD'}
        mountOnEnter
        unmountOnExit
        collapse
        bottom
      >
        <MarginComponent top={20}>
          <FloatingLabelComponent
            lifted={!!confirmationFormValues.password}
            titleText={
              <TextComponent color="secondary" size="m">
                {intl.passwordInputPlaceholder}
              </TextComponent>
            }
            hintText={
              <TextComponent color="critical" size="s">
                {passwordHint}
              </TextComponent>
            }
          >
            <TextInputControlComponent
              name="password"
              type="password"
              value={confirmationFormValues.password}
              disabled={processName === 'CONFIRMATION_FORM_SUBMISSION'}
              invalid={passwordIsInvalid}
              maxLength={INPUT_PASSWORD_MAX_LENGTH}
              onValueChange={setPasswordValue}
              autoFocus={confirmationType === 'WITH_PASSWORD'}
            />
          </FloatingLabelComponent>
          <MarginComponent top={12}>
            <TextComponent>
              {ownDictionary.restorePasswordText}{' '}
              <LinkControlComponent local to={routes.restorePassword()}>
                <TextComponent color="accent">
                  {ownDictionary.restorePasswordLinkText}
                </TextComponent>
              </LinkControlComponent>
            </TextComponent>
          </MarginComponent>
        </MarginComponent>
      </Fade>

      {emailAlreadyInUse && (
        <ParagraphComponent>
          {ownDictionary.emailAlreadyInUseText()}
        </ParagraphComponent>
      )}

      <Fade
        duration={ANIMATION_DEFAULT_DURATION}
        delay={!confirmationType ? ANIMATION_DEFAULT_DURATION : 0}
        when={!confirmationType}
        mountOnEnter
        unmountOnExit
        collapse
        bottom
      >
        <MarginComponent top={48}>
          <span>
            <PressControlComponent
              disabled={processName === 'EMAIL_FORM_SUBMISSION'}
              theme="primary"
              type="submit"
              hasSpinner={processName === 'EMAIL_FORM_SUBMISSION'}
            >
              {ownDictionary.submitEmailFormActionText}
            </PressControlComponent>
          </span>
        </MarginComponent>
      </Fade>

      <Fade
        duration={ANIMATION_DEFAULT_DURATION}
        delay={
          confirmationType === 'WITH_PASSWORD' ? ANIMATION_DEFAULT_DURATION : 0
        }
        when={confirmationType === 'WITH_PASSWORD'}
        mountOnEnter
        unmountOnExit
        collapse
        bottom
      >
        <MarginComponent top={138}>
          <span>
            <PressControlComponent
              disabled={processName === 'CONFIRMATION_FORM_SUBMISSION'}
              theme="primary"
              type="submit"
              hasSpinner={processName === 'CONFIRMATION_FORM_SUBMISSION'}
            >
              {ownDictionary.submitConfirmationFormActionText}
            </PressControlComponent>
          </span>
        </MarginComponent>
      </Fade>
    </FormControlComponent>
  );
};
